import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';
import type { ProductRegimeProductModel } from 'autogen/swagger/all';
import { CurrentProduct } from 'autogen/translation-keys/trans-website-productregime';
import { AddToCartAriaText } from 'autogen/translation-keys/trans-website-shared-buttons';
import Card from 'components/generic/card';
import { StockNotificationButtonCompact } from 'components/generic/productCTA/components/OpenStockNotification/StockNotificationButton';
import { Typography } from 'components/generic/typography';
import Checkbox from 'components/shared/inputs/checkbox';
import ProductItem from 'components/shared/productItem';
import { createProductItemTrackingContext } from 'components/shared/productItem/shared/helpers';
import { VisuallyHidden } from 'helpers/visuallyHidden';
import { firstCharacterToLower, format } from 'modules/helpers/strings';
import { useRegime } from './Regime';
import RegimeVariantSelector from './RegimeVariantSelector';

/* #region  styles */
const StyledCard = styled(Card)<{ addHeaderSpacing: boolean }>`
    border: 1px solid ${({ theme }) => theme.ColorBorderTertiary};
    overflow: unset;

    ${({ addHeaderSpacing }) =>
        addHeaderSpacing &&
        css`
            margin-top: 30.5px;
        `}
`;

const StyledCardHeader = styled(Card.Header)`
    justify-content: center;
    background-color: ${({ theme }) => theme.ComponentsProductCardOverlay};
`;

const StyledCardContent = styled(Card.Content)`
    position: relative;
    display: flex;
    flex-direction: column;
    padding: 0;
`;

const StyledCardFooter = styled(Card.Footer)`
    display: block;
    background-color: ${({ theme }) => theme.ColorBackgroundPrimary};
    padding: 0 ${({ theme }) => `${theme.Spacing16} ${theme.Spacing16}`};
    height: 56px;
`;

const Label = styled.label`
    display: flex;
    position: absolute;
    right: ${({ theme }) => theme.Spacing16};
    top: ${({ theme }) => theme.Spacing16};
    z-index: 2;
`;

const StyledProductItem = styled(ProductItem)<{ isSoldOut: boolean }>`
    height: 100%;

    ${({ isSoldOut }) =>
        isSoldOut &&
        css`
            opacity: 0.4;
        `}
`;

const StyledButton = styled(StockNotificationButtonCompact)`
    position: absolute;
    right: ${({ theme }) => theme.Spacing16};
    bottom: ${({ theme }) => theme.Spacing16};
    width: 40px;
`;

/* #endregion */

export default function RegimeCardVertical({ product }: { product: ProductRegimeProductModel }) {
    const { product: productItem, variants, stockAvailable } = product;
    const {
        isSelected,
        toggleProduct,
        mainProductRegimeId,
        excludeMainProduct,
        hasProductWithVariants,
        deselectProduct
    } = useRegime();
    const selected = isSelected(productItem.id);
    const { t } = useTranslation();
    const isCurrentProduct = productItem.id === mainProductRegimeId;
    const showFooter = hasProductWithVariants();
    const trackingContext = createProductItemTrackingContext(firstCharacterToLower(productItem.uaTrackingCategory));

    // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
    useEffect(() => {
        if (!stockAvailable) {
            deselectProduct(productItem.id);
        }
    }, []);

    return (
        <StyledCard addHeaderSpacing={!isCurrentProduct && !excludeMainProduct}>
            {isCurrentProduct && (
                <StyledCardHeader>
                    <Typography.Body size="S" strong as="span">
                        {t(CurrentProduct)}
                    </Typography.Body>
                </StyledCardHeader>
            )}

            <StyledCardContent>
                <StyledProductItem model={productItem} themeType="regime" isSoldOut={!stockAvailable} />

                {!stockAvailable && (
                    <StyledButton
                        params={{
                            action: productItem.action,
                            trackingContext,
                            productId: productItem.id,
                            externalProductId: productItem.externalProductId,
                            productNavigationLink: productItem.linkAction,
                            fromPowerstep: false,
                            showPowerstepOnAddToBasket: false,
                            addToBasketType: productItem.addToBasketType
                        }}
                        trackingInfo={trackingContext}
                    />
                )}

                <Label>
                    <Checkbox
                        value={productItem.id}
                        checked={selected && stockAvailable}
                        disabled={!stockAvailable}
                        onChange={() => toggleProduct(productItem.id)}
                    />
                    <VisuallyHidden>{format(t(AddToCartAriaText))(productItem.standardName)}</VisuallyHidden>
                </Label>
            </StyledCardContent>

            {showFooter && (
                <StyledCardFooter>
                    {variants.length > 1 && <RegimeVariantSelector product={product} />}
                </StyledCardFooter>
            )}
        </StyledCard>
    );
}
